import React, { useState } from 'react';
import { DB } from '../data/database';
import { CURRENCIES } from '../data/constants';
import { useLanguage } from '../translations/LanguageContext';
import { DollarSignIcon, CheckIcon, CarIcon, EngineIcon, PartsIcon } from './Icons';
import { Notifications } from '../data/notifications';
import CustomerFilter, { CustomerFilterBanner } from './CustomerFilter';

// ── Helpers ───────────────────────────────────────────────────
const JPY_FIELDS = ['unit_price_jpy','recycle_fee_jpy','misc_jpy','bid_fee_jpy','transport_jpy','vanning_jpy','container_share_jpy','freight_jpy','commission_jpy'];
const DEST_FIELDS = ['freight_dest','clearing_exp','yard_charges','local_transport','other_charges'];

function sumJpy(p) { return JPY_FIELDS.reduce((s, k) => s + (Number(p[k]) || 0), 0); }
function sumDest(p) { return DEST_FIELDS.reduce((s, k) => s + (Number(p[k]) || 0), 0); }
function getRate() {
  const rates = (DB.get('appConfig') || {}).exchange_rates || CURRENCIES;
  const jpyRate = rates.JPY?.rate || rates.JPY || 149.5;
  return typeof jpyRate === 'object' ? jpyRate.rate : jpyRate;
}

function marginColor(pct) {
  if (pct < 0) return { color: 'var(--red)', label: 'LOSS' };
  if (pct < 10) return { color: 'var(--yellow)', label: '🟡' };
  return { color: 'var(--green)', label: '✅' };
}

function timeAgo(iso) {
  if (!iso) return '';
  const ms = Date.now() - new Date(iso).getTime();
  const h = Math.floor(ms / 3600000);
  if (h < 1) return 'just now';
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

function getUnitIcon(type) { return type === 'vehicle' ? <CarIcon /> : type === 'engine' ? <EngineIcon /> : <PartsIcon />; }

function loadFeeDefaults() {
  try {
    const cfg = DB.get('appConfig');
    if (cfg && typeof cfg === 'object' && !Array.isArray(cfg)) return cfg.fee_defaults || {};
    if (Array.isArray(cfg)) { const r = cfg.find(x => x.key === 'fee_defaults'); return r?.value || {}; }
  } catch {} return {};
}

// Stable num input
const NumField = React.memo(function NumField({ label, field, pricing, onChange, prefix = '¥' }) {
  return (
    <div className="form-group">
      <label className="form-label" style={{ fontSize: 11 }}>{label}</label>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <span style={{ fontSize: 11, color: 'var(--text3)', marginRight: 4 }}>{prefix}</span>
        <input type="number" className="form-input" value={pricing[field] ?? ''}
          onChange={e => onChange(field, e.target.value)} style={{ fontSize: 13 }} />
      </div>
    </div>
  );
});

// ══════════════════════════════════════════════════════════════
// ACCOUNTANT: Price a single unit within a batch quote
// ══════════════════════════════════════════════════════════════
function UnitPricingCard({ unitEntry, unitData, feeDefaults, expanded, onToggle, onChange }) {
  const existing = unitEntry.pricing || {};
  const rate = getRate();

  const [pricing, setPricing] = useState(() => {
    const isNew = !existing || Object.keys(existing).length === 0;
    return {
      unit_price_jpy: existing.unit_price_jpy ?? (unitData?.costJPY || unitData?.pricing?.unit_price_jpy || ''),
      recycle_fee_jpy: existing.recycle_fee_jpy ?? '',
      misc_jpy: existing.misc_jpy ?? '',
      bid_fee_jpy: existing.bid_fee_jpy ?? (isNew ? (feeDefaults.auction_fee_fixed_jpy || '') : ''),
      transport_jpy: existing.transport_jpy ?? (isNew ? (feeDefaults.transport_default_jpy || '') : ''),
      vanning_jpy: existing.vanning_jpy ?? (isNew ? (feeDefaults.vanning_default_jpy || '') : ''),
      container_share_jpy: existing.container_share_jpy ?? '',
      freight_jpy: existing.freight_jpy ?? '',
      commission_jpy: existing.commission_jpy ?? '',
      freight_dest: existing.freight_dest ?? '',
      clearing_exp: existing.clearing_exp ?? '',
      yard_charges: existing.yard_charges ?? '',
      local_transport: existing.local_transport ?? '',
      other_charges: existing.other_charges ?? '',
    };
  });
  const [fobPrice, setFobPrice] = useState(existing.fob_price || unitEntry.fob_price || '');
  const [notes, setNotes] = useState(unitEntry.accountant_notes || '');

  const handleField = (field, val) => {
    setPricing(prev => {
      const next = { ...prev, [field]: val };
      // Propagate to parent after state update
      setTimeout(() => propagate(next, fobPrice, notes), 0);
      return next;
    });
  };

  const propagate = (p, fob, n) => {
    const totalJpy = sumJpy(p);
    const totalDest = sumDest(p);
    const costUsd = totalJpy / rate + totalDest;
    const f = Number(fob) || 0;
    const marginUsd = f - costUsd;
    const marginPct = costUsd > 0 ? (marginUsd / costUsd) * 100 : 0;
    onChange(unitEntry.unit_id, {
      pricing: { ...p, total_jpy: totalJpy, fob_price: f || null },
      fob_price: f || null,
      accountant_notes: n,
      _computed: { totalJpy, totalDest, costUsd, fob: f, marginUsd, marginPct },
    });
  };

  const handleFob = (val) => {
    setFobPrice(val);
    setTimeout(() => propagate(pricing, val, notes), 0);
  };

  const handleNotes = (val) => {
    setNotes(val);
    setTimeout(() => propagate(pricing, fobPrice, val), 0);
  };

  const totalJpy = sumJpy(pricing);
  const totalDest = sumDest(pricing);
  const costUsd = totalJpy / rate + totalDest;
  const fob = Number(fobPrice) || 0;
  const marginUsd = fob - costUsd;
  const marginPct = costUsd > 0 ? (marginUsd / costUsd) * 100 : 0;
  const mc = marginColor(marginPct);

  const F = (label, field, prefix) => (
    <NumField key={field} label={label} field={field} pricing={pricing} onChange={handleField} prefix={prefix || '¥'} />
  );

  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: 10, marginBottom: 12, overflow: 'hidden' }}>
      {/* Header — always visible */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px', background: 'var(--bg3)', cursor: 'pointer' }} onClick={onToggle}>
        <span style={{ color: 'var(--text3)', width: 20 }}>{getUnitIcon(unitEntry.unit_type)}</span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 700 }}>{unitEntry.unit_description}</div>
          <div style={{ fontSize: 10, color: 'var(--cyan)', fontFamily: 'monospace' }}>{unitEntry.unit_sku}</div>
          {unitData && (
            <div style={{ fontSize: 10, color: 'var(--text3)' }}>
              {unitData.grade ? `Grade: ${unitData.grade} · ` : ''}{unitData.mileage ? `${Number(unitData.mileage).toLocaleString()} km` : ''}
            </div>
          )}
        </div>
        <div style={{ textAlign: 'right', flexShrink: 0 }}>
          {fob > 0 ? (
            <>
              <div style={{ fontSize: 14, fontWeight: 800, color: 'var(--green)' }}>FOB: ${fob.toLocaleString()}</div>
              <div style={{ fontSize: 10, color: mc.color }}>{marginPct.toFixed(1)}% {mc.label}</div>
            </>
          ) : (
            <span style={{ fontSize: 11, color: 'var(--yellow)' }}>Not priced</span>
          )}
        </div>
        <span style={{ color: 'var(--text3)', fontSize: 12 }}>{expanded ? '▲' : '▼'}</span>
      </div>

      {/* Expanded pricing form */}
      {expanded && (
        <div style={{ padding: 16 }}>
          {/* Japan Costs */}
          <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 8 }}>Japan Costs (JPY)</div>
          <div className="form-row">{F('Auction/Unit Price', 'unit_price_jpy')}{F('Recycle Fee', 'recycle_fee_jpy')}{F('Misc', 'misc_jpy')}</div>
          <div className="form-row">{F('Bid Fee', 'bid_fee_jpy')}{F('Transport', 'transport_jpy')}{F('Vanning', 'vanning_jpy')}</div>
          <div className="form-row">{F('Container Share', 'container_share_jpy')}{F('Freight', 'freight_jpy')}{F('Commission', 'commission_jpy')}</div>
          <div style={{ padding: '8px 12px', background: 'var(--bg2)', borderRadius: 6, display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
            <span style={{ fontWeight: 700, fontSize: 12 }}>Total JPY</span>
            <span style={{ fontWeight: 800 }}>¥{totalJpy.toLocaleString()} <span style={{ color: 'var(--text3)', fontSize: 11 }}>(${Math.round(totalJpy / rate).toLocaleString()})</span></span>
          </div>

          {/* Destination */}
          <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 8 }}>Destination Charges (USD)</div>
          <div className="form-row">{F('Clearing', 'clearing_exp', '$')}{F('Yard', 'yard_charges', '$')}{F('Local Transport', 'local_transport', '$')}</div>
          <div className="form-row" style={{ gridTemplateColumns: '1fr 1fr 1fr' }}>{F('Freight (Dest)', 'freight_dest', '$')}{F('Other', 'other_charges', '$')}<div /></div>
          {totalDest > 0 && (
            <div style={{ padding: '6px 12px', background: 'var(--bg2)', borderRadius: 6, display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
              <span style={{ fontSize: 12, fontWeight: 600 }}>Destination Total</span>
              <span style={{ fontWeight: 700 }}>${totalDest.toLocaleString()}</span>
            </div>
          )}

          {/* FOB + Margin */}
          <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 8 }}>FOB Price</div>
          <div className="form-group" style={{ marginBottom: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <span style={{ fontSize: 14, color: 'var(--text3)', marginRight: 6 }}>$</span>
              <input type="number" className="form-input" value={fobPrice} onChange={e => handleFob(e.target.value)}
                style={{ fontSize: 16, fontWeight: 700, maxWidth: 200 }} />
            </div>
          </div>
          {fob > 0 && (
            <div style={{ padding: 12, background: marginPct < 0 ? 'rgba(248,81,73,0.08)' : marginPct < 10 ? 'rgba(210,153,34,0.08)' : 'rgba(63,185,80,0.08)', borderRadius: 8, marginBottom: 12 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
                <span>Cost: ${Math.round(costUsd).toLocaleString()}</span>
                <span>FOB: ${fob.toLocaleString()}</span>
                <span style={{ fontWeight: 800, color: mc.color }}>Margin: {marginUsd >= 0 ? '+' : ''}${Math.round(marginUsd).toLocaleString()} ({marginPct.toFixed(1)}%) {mc.label}</span>
              </div>
            </div>
          )}

          {/* Notes */}
          <div className="form-group">
            <label className="form-label" style={{ fontSize: 11 }}>Accountant Notes</label>
            <input className="form-input" value={notes} onChange={e => handleNotes(e.target.value)}
              placeholder="Notes about this unit's pricing..." style={{ fontSize: 12 }} />
          </div>
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// ACCOUNTANT: Full pricing view for a batch quote
// ══════════════════════════════════════════════════════════════
function AccountantPricingView({ quote, user, onBack }) {
  const [expandedUnit, setExpandedUnit] = useState(null);
  const [unitUpdates, setUnitUpdates] = useState({});
  const [saved, setSaved] = useState('');

  const allUnits = [...(DB.get('vehicles') || []), ...(DB.get('engines') || []), ...(DB.get('spareParts') || [])];
  const customers = DB.get('customers') || [];
  const customer = customers.find(c => c.id === (quote.customerId || quote.requestedBy));
  const feeDefaults = loadFeeDefaults();

  const units = quote.units || [];

  const handleUnitChange = (unitId, data) => {
    setUnitUpdates(prev => ({ ...prev, [unitId]: data }));
    setSaved('');
  };

  const getMergedUnit = (u) => {
    const upd = unitUpdates[u.unit_id];
    if (!upd) return u;
    return { ...u, pricing: upd.pricing || u.pricing, fob_price: upd.fob_price ?? u.fob_price, accountant_notes: upd.accountant_notes ?? u.accountant_notes };
  };

  // Read-only if pending_approval
  const isReadOnly = quote.status === 'pending_approval';

  const saveQuote = (submitToAdmin) => {
    if (isReadOnly) return;
    const allQuotes = DB.get('priceQuotes') || [];
    const updatedUnits = units.map(u => {
      const upd = unitUpdates[u.unit_id];
      if (!upd) return u;
      return {
        ...u,
        pricing: upd.pricing || u.pricing,
        fob_price: upd.fob_price ?? u.fob_price,
        accountant_notes: upd.accountant_notes ?? u.accountant_notes,
        status: upd.fob_price ? 'priced' : u.status,
      };
    });

    const newStatus = submitToAdmin ? 'pending_approval' : 'in_progress';
    const updated = {
      ...quote,
      units: updatedUnits,
      status: newStatus,
      assigned_to: user.id,
      assignedToName: user.name,
      ...(submitToAdmin ? { submitted_for_approval_at: new Date().toISOString() } : {}),
    };

    DB.set('priceQuotes', allQuotes.map(q => q.id === quote.id ? updated : q));

    // Save COST breakdown to unit records (NOT fob_price — that's per-customer on the quote)
    updatedUnits.forEach(u => {
      if (!u.pricing) return;
      const unitData = allUnits.find(x => x.id === u.unit_id);
      if (!unitData) return;
      const key = unitData.make ? 'vehicles' : unitData.engineName ? 'engines' : 'spareParts';
      const list = DB.get(key) || [];
      // Strip fob_price from what we save to the unit — costs are shared, FOB is per-customer
      const { fob_price, fob_price_usd, ...costOnly } = (u.pricing || {});
      DB.set(key, list.map(x => x.id === u.unit_id ? { ...x, pricing: { ...(x.pricing || {}), ...costOnly } } : x));
    });

    if (submitToAdmin) {
      const pricedCount = updatedUnits.filter(u => u.fob_price).length;
      // Notify admins (global staff notification visible to all admin/head_admin)
      Notifications.add('price_updated', 'Price Quote Ready for Approval',
        `${user.name} priced quote #${quote.id} — ${pricedCount}/${units.length} units for ${customer?.name || 'customer'}. Review and approve FOB prices.`, {});
      setSaved('Submitted to admin for approval!');
    } else {
      setSaved('Draft saved.');
    }
  };

  // Summary stats
  const pricedCount = units.filter(u => {
    const m = getMergedUnit(u);
    return m.fob_price || m.pricing?.fob_price;
  }).length;

  const computedStats = units.map(u => {
    const upd = unitUpdates[u.unit_id];
    return upd?._computed || null;
  }).filter(Boolean);

  const avgMargin = computedStats.length > 0 ? computedStats.reduce((s, c) => s + c.marginPct, 0) / computedStats.length : 0;
  const lowestMargin = computedStats.length > 0 ? computedStats.reduce((min, c) => c.marginPct < min.marginPct ? c : min, computedStats[0]) : null;

  return (
    <div>
      <button className="btn btn-secondary btn-sm" onClick={onBack} style={{ marginBottom: 12 }}>← Back to Queue</button>

      <h2 style={{ fontSize: 16, fontWeight: 700, marginBottom: 4 }}>Price Quote #{quote.id}</h2>
      <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 16 }}>
        Customer: <strong>{customer?.name || quote.requestedByName}</strong>
        {customer?.company ? ` — ${customer.company}` : ''}
        {' · '}Requested: {quote.createdAt} ({timeAgo(quote.submittedAt || quote.createdAt)})
      </div>

      {/* Read-only banner */}
      {isReadOnly && (
        <div style={{ padding: '12px 16px', background: 'rgba(163,113,247,0.08)', border: '1px solid rgba(163,113,247,0.2)', borderRadius: 8, marginBottom: 16, fontSize: 12, color: 'var(--purple)', textAlign: 'center' }}>
          Submitted for approval — waiting for admin review
        </div>
      )}

      {/* Revision notes from admin */}
      {quote.revision_notes && (
        <div style={{ padding: '12px 16px', background: 'rgba(248,81,73,0.06)', border: '1px solid rgba(248,81,73,0.2)', borderRadius: 8, marginBottom: 16, fontSize: 12 }}>
          <span style={{ fontWeight: 700, color: 'var(--red)' }}>Admin revision notes:</span> <span style={{ color: 'var(--text2)' }}>"{quote.revision_notes}"</span>
        </div>
      )}

      {quote.customer_notes && (
        <div style={{ padding: '10px 14px', background: 'rgba(88,166,255,0.06)', border: '1px solid rgba(88,166,255,0.2)', borderRadius: 8, marginBottom: 16, fontSize: 12 }}>
          <span style={{ fontWeight: 600 }}>Customer note:</span> "{quote.customer_notes}"
        </div>
      )}

      {/* Unit pricing cards */}
      {units.map((u, i) => {
        const unitData = allUnits.find(x => x.id === u.unit_id);
        return (
          <UnitPricingCard
            key={u.unit_id || i}
            unitEntry={getMergedUnit(u)}
            unitData={unitData}
            feeDefaults={feeDefaults}
            expanded={expandedUnit === u.unit_id}
            onToggle={() => setExpandedUnit(expandedUnit === u.unit_id ? null : u.unit_id)}
            onChange={handleUnitChange}
          />
        );
      })}

      {/* Summary */}
      <div style={{ padding: 16, background: 'var(--bg3)', borderRadius: 10, border: '1px solid var(--border)', marginBottom: 16 }}>
        <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 8 }}>Summary</div>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 4 }}>
          <span>Total units</span><span style={{ fontWeight: 700 }}>{units.length}</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 4 }}>
          <span>All priced</span>
          <span style={{ fontWeight: 700, color: pricedCount === units.length ? 'var(--green)' : 'var(--yellow)' }}>
            {pricedCount === units.length ? '✅ Yes' : `❌ ${units.length - pricedCount} still need FOB price`}
          </span>
        </div>
        {computedStats.length > 0 && (
          <>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 4 }}>
              <span>Average margin</span><span style={{ fontWeight: 700 }}>{avgMargin.toFixed(1)}%</span>
            </div>
            {lowestMargin && (
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: lowestMargin.marginPct < 10 ? 'var(--yellow)' : 'var(--text2)' }}>
                <span>Lowest margin</span><span>{lowestMargin.marginPct.toFixed(1)}%</span>
              </div>
            )}
          </>
        )}
      </div>

      {saved && (
        <div style={{ padding: '10px 14px', background: 'rgba(63,185,80,0.12)', borderRadius: 8, color: 'var(--green)', fontSize: 12, marginBottom: 12, textAlign: 'center' }}>✓ {saved}</div>
      )}

      {!isReadOnly ? (
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <button className="btn btn-secondary" onClick={onBack}>Cancel</button>
          <button className="btn btn-primary" onClick={() => saveQuote(false)}>Save Draft</button>
          <button className="btn btn-success" onClick={() => saveQuote(true)}>
            <CheckIcon /> Submit to Admin for Approval
          </button>
        </div>
      ) : (
        <div style={{ display: 'flex', gap: 10 }}>
          <button className="btn btn-secondary" onClick={onBack}>← Back</button>
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// ADMIN: Approval view for a batch quote — per-unit decisions
// ══════════════════════════════════════════════════════════════
function AdminApprovalView({ quote, user, onBack }) {
  const [adjusting, setAdjusting] = useState({});     // unitId → adjusted FOB string
  const [decisions, setDecisions] = useState({});      // unitId → 'approved' | 'rejected'
  const [rejectReasons, setRejectReasons] = useState({}); // unitId → reason string
  const [rejectingUnit, setRejectingUnit] = useState(null); // unitId being rejected
  const [sendingBack, setSendingBack] = useState(false);
  const [revisionNote, setRevisionNote] = useState('');
  const [adminNotes, setAdminNotes] = useState('');
  const [expandedUnit, setExpandedUnit] = useState(null);
  const [done, setDone] = useState('');

  const allUnits = [...(DB.get('vehicles') || []), ...(DB.get('engines') || []), ...(DB.get('spareParts') || [])];
  const customers = DB.get('customers') || [];
  const customer = customers.find(c => c.id === (quote.customerId || quote.requestedBy));
  const rate = getRate();
  const units = quote.units || [];

  const getUnitCost = (u) => {
    const p = u.pricing || {};
    return sumJpy(p) / rate + sumDest(p);
  };

  const getFob = (u) => {
    if (adjusting[u.unit_id] !== undefined) return Number(adjusting[u.unit_id]) || 0;
    return u.fob_price || u.pricing?.fob_price || 0;
  };

  const getMargin = (u) => {
    const cost = getUnitCost(u);
    const fob = getFob(u);
    if (!cost || !fob) return 0;
    return ((fob - cost) / cost) * 100;
  };

  const totalCost = units.reduce((s, u) => s + getUnitCost(u), 0);
  const totalFob = units.reduce((s, u) => s + getFob(u), 0);
  const avgMargin = totalCost > 0 ? ((totalFob - totalCost) / totalCost) * 100 : 0;
  const approvedCount = Object.values(decisions).filter(d => d === 'approved').length;
  const rejectedCount = Object.values(decisions).filter(d => d === 'rejected').length;
  const undecidedCount = units.length - approvedCount - rejectedCount;

  const approveUnit = (uid) => {
    setDecisions(prev => ({ ...prev, [uid]: 'approved' }));
    setRejectingUnit(null);
  };

  const rejectUnit = (uid, reason) => {
    setDecisions(prev => ({ ...prev, [uid]: 'rejected' }));
    setRejectReasons(prev => ({ ...prev, [uid]: reason }));
    setRejectingUnit(null);
  };

  const approveAllRemaining = () => {
    const next = { ...decisions };
    units.forEach(u => { if (!next[u.unit_id]) next[u.unit_id] = 'approved'; });
    setDecisions(next);
  };

  // ── FINALIZE ──
  const finalize = () => {
    const allQuotes = DB.get('priceQuotes') || [];
    const updatedUnits = units.map(u => {
      const dec = decisions[u.unit_id] || 'approved'; // default approve undecided
      const finalFob = getFob(u);
      const accountantFob = u.fob_price || u.pricing?.fob_price || 0;
      const adminAdjusted = adjusting[u.unit_id] !== undefined ? Number(adjusting[u.unit_id]) : null;

      if (dec === 'approved') {
        return {
          ...u,
          accountant_fob: accountantFob,
          admin_adjusted_fob: adminAdjusted,
          final_fob: finalFob,
          fob_price: finalFob,
          pricing: { ...(u.pricing || {}), fob_price: finalFob, fob_price_usd: finalFob },
          status: 'approved',
          admin_approved: true,
          admin_note: adminNotes || null,
        };
      } else {
        return {
          ...u,
          status: 'rejected',
          admin_approved: false,
          rejection_reason: rejectReasons[u.unit_id] || '',
          admin_note: adminNotes || null,
        };
      }
    });

    const approvedU = updatedUnits.filter(u => u.status === 'approved');
    const rejectedU = updatedUnits.filter(u => u.status === 'rejected');

    let quoteStatus;
    if (rejectedU.length === 0) quoteStatus = 'approved';
    else if (approvedU.length === 0) quoteStatus = 'revision_needed';
    else quoteStatus = 'partially_approved';

    const updated = {
      ...quote,
      units: updatedUnits,
      status: quoteStatus,
      approved_by: user.id,
      approved_at: new Date().toISOString(),
      admin_notes: adminNotes || quote.admin_notes,
      // 12-hour price expiry window — customer must finalize selection within this time
      expires_at: new Date(Date.now() + 12 * 60 * 60 * 1000).toISOString(),
    };

    DB.set('priceQuotes', allQuotes.map(q => q.id === quote.id ? updated : q));

    // Save COST breakdown to unit records (NOT fob_price — FOB is per-customer, stays on quote)
    approvedU.forEach(u => {
      if (!u.pricing) return;
      const unitData = allUnits.find(x => x.id === u.unit_id);
      if (!unitData) return;
      const key = unitData.make ? 'vehicles' : unitData.engineName ? 'engines' : 'spareParts';
      const list = DB.get(key) || [];
      const { fob_price, fob_price_usd, ...costOnly } = (u.pricing || {});
      DB.set(key, list.map(x => x.id === u.unit_id ? {
        ...x,
        pricing: { ...(x.pricing || {}), ...costOnly },
        ...(costOnly.total_jpy ? { priceJPY: costOnly.total_jpy } : {}),
      } : x));
    });

    // Notifications — correct routing per role
    const custId = quote.customerId || quote.requestedBy;
    if (quoteStatus === 'approved') {
      // Accountant: "your quote was approved"
      Notifications.add('price_updated', 'Quote Approved ✅',
        `Price quote #${quote.id} approved by ${user.name} — all ${approvedU.length} units`, {});
      // Customer: "your prices are ready" (separate notification)
      Notifications.add('price_updated', 'Prices Ready',
        `FOB prices are ready for ${approvedU.length} unit${approvedU.length !== 1 ? 's' : ''} in your stock. Log in to review.`, {}, custId);
    } else if (quoteStatus === 'partially_approved') {
      // Accountant: told about rejections (NOT customer)
      Notifications.add('price_updated', 'Quote Needs Revision',
        `Price quote #${quote.id} — ${rejectedU.length} unit${rejectedU.length !== 1 ? 's' : ''} rejected. ${adminNotes ? 'Admin: ' + adminNotes : 'See rejection reasons.'}`, {});
      // Customer: only told about approved units
      if (approvedU.length > 0) {
        Notifications.add('price_updated', 'Prices Ready',
          `FOB prices are ready for ${approvedU.length} unit${approvedU.length !== 1 ? 's' : ''} in your stock. Log in to review.`, {}, custId);
      }
    } else {
      // All rejected — accountant only (NOT customer)
      Notifications.add('price_updated', 'Quote Needs Revision',
        `Price quote #${quote.id} sent back for revision — all ${units.length} units need repricing. ${adminNotes || ''}`, {});
    }

    setDone(quoteStatus === 'approved'
      ? `All ${approvedU.length} prices approved and saved!`
      : `${approvedU.length} approved, ${rejectedU.length} sent back for revision.`);
  };

  // ── Send back entire quote ──
  const sendBackAll = () => {
    const allQuotes = DB.get('priceQuotes') || [];
    DB.set('priceQuotes', allQuotes.map(q => q.id === quote.id ? {
      ...quote, status: 'revision_needed', revision_notes: revisionNote, admin_notes: revisionNote,
    } : q));
    Notifications.add('price_updated', 'Quote Needs Revision',
      `Price quote #${quote.id} sent back for revision. Admin: ${revisionNote}`, {});
    setDone('Sent back to accountant for revision.');
  };

  if (done) {
    return (
      <div style={{ textAlign: 'center', padding: 40 }}>
        <div style={{ fontSize: 40, marginBottom: 12 }}>✅</div>
        <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 8 }}>{done}</div>
        <button className="btn btn-primary" onClick={onBack}>Back to Quotes</button>
      </div>
    );
  }

  return (
    <div>
      <button className="btn btn-secondary btn-sm" onClick={onBack} style={{ marginBottom: 12 }}>← Back</button>

      <h2 style={{ fontSize: 16, fontWeight: 700, marginBottom: 4 }}>Price Quote #{quote.id} — Awaiting Approval</h2>
      <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 4 }}>
        Customer: <strong>{customer?.name || quote.requestedByName}</strong>
        {customer?.company ? ` — ${customer.company}` : ''}
      </div>
      {quote.assignedToName && <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 4 }}>Prepared by: {quote.assignedToName} · {timeAgo(quote.submitted_for_approval_at)}</div>}

      {/* Accountant notes */}
      {(() => {
        const allNotes = units.map(u => u.accountant_notes).filter(Boolean);
        if (allNotes.length === 0) return null;
        return (
          <div style={{ padding: '10px 14px', background: 'rgba(88,166,255,0.06)', border: '1px solid rgba(88,166,255,0.2)', borderRadius: 8, marginBottom: 16, fontSize: 12 }}>
            <span style={{ fontWeight: 700 }}>Accountant Notes:</span>
            {allNotes.map((n, i) => <div key={i} style={{ color: 'var(--text2)', marginTop: 2 }}>"{n}"</div>)}
          </div>
        );
      })()}

      {/* ── Per-unit cards ── */}
      {units.map((u, i) => {
        const unitData = allUnits.find(x => x.id === u.unit_id);
        const cost = getUnitCost(u);
        const fob = getFob(u);
        const margin = getMargin(u);
        const mc = marginColor(margin);
        const dec = decisions[u.unit_id];
        const isExp = expandedUnit === u.unit_id;
        const isAdj = adjusting[u.unit_id] !== undefined;
        const p = u.pricing || {};
        const totalJpy = sumJpy(p);

        return (
          <div key={u.unit_id || i} style={{
            border: `1px solid ${dec === 'approved' ? 'rgba(63,185,80,0.4)' : dec === 'rejected' ? 'rgba(248,81,73,0.4)' : 'var(--border)'}`,
            borderRadius: 10, marginBottom: 10, overflow: 'hidden',
            background: dec === 'approved' ? 'rgba(63,185,80,0.04)' : dec === 'rejected' ? 'rgba(248,81,73,0.04)' : 'transparent',
          }}>
            {/* Card header */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px', cursor: 'pointer' }} onClick={() => setExpandedUnit(isExp ? null : u.unit_id)}>
              {unitData?.images?.[0] && <img src={unitData.images[0]} alt="" style={{ width: 56, height: 42, borderRadius: 6, objectFit: 'cover', flexShrink: 0 }} />}
              {!unitData?.images?.[0] && <span style={{ width: 24, color: 'var(--text3)' }}>{getUnitIcon(u.unit_type)}</span>}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 700 }}>{u.unit_description}</div>
                <div style={{ fontSize: 10, color: 'var(--cyan)', fontFamily: 'monospace' }}>{u.unit_sku}</div>
                {unitData && <div style={{ fontSize: 10, color: 'var(--text3)' }}>
                  {unitData.grade ? `Grade: ${unitData.grade} · ` : ''}{unitData.mileage ? `${Number(unitData.mileage).toLocaleString()} km` : ''}
                </div>}
              </div>
              <div style={{ textAlign: 'right', flexShrink: 0 }}>
                {dec === 'approved' && <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--green)' }}>✅ Approved</div>}
                {dec === 'rejected' && <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--red)' }}>❌ Rejected</div>}
                {!dec && <>
                  <div style={{ fontSize: 13, fontWeight: 800 }}>${fob.toLocaleString()}</div>
                  <div style={{ fontSize: 10, fontWeight: 700, color: mc.color }}>{margin.toFixed(1)}% {mc.label}</div>
                </>}
              </div>
              <span style={{ color: 'var(--text3)', fontSize: 12 }}>{isExp ? '▲' : '▼'}</span>
            </div>

            {/* Expanded detail */}
            {isExp && (
              <div style={{ padding: '0 16px 14px', borderTop: '1px solid var(--border)' }}>
                {/* Cost breakdown */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 12 }}>
                  <div>
                    <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text3)', marginBottom: 4 }}>Japan Costs</div>
                    <div style={{ fontSize: 11, color: 'var(--text2)' }}>¥{totalJpy.toLocaleString()} (${Math.round(totalJpy / rate).toLocaleString()})</div>
                  </div>
                  <div>
                    <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text3)', marginBottom: 4 }}>Destination</div>
                    <div style={{ fontSize: 11, color: 'var(--text2)' }}>${sumDest(p).toLocaleString()}</div>
                  </div>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderTop: '1px solid var(--border)', marginTop: 8, fontSize: 12 }}>
                  <span style={{ fontWeight: 700 }}>Total Cost</span>
                  <span style={{ fontWeight: 700 }}>${Math.round(cost).toLocaleString()}</span>
                </div>

                {/* FOB — editable by admin */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderTop: '1px solid var(--border)' }}>
                  <span style={{ fontSize: 12, fontWeight: 700 }}>FOB Price:</span>
                  {isAdj ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      <span style={{ fontSize: 13, color: 'var(--text3)' }}>$</span>
                      <input type="number" className="form-input" value={adjusting[u.unit_id]}
                        onChange={e => setAdjusting(prev => ({ ...prev, [u.unit_id]: e.target.value }))}
                        style={{ width: 110, fontSize: 14, fontWeight: 700 }} />
                      <button className="btn btn-success btn-sm" style={{ fontSize: 10 }}
                        onClick={() => { /* keep the adjusted value, just exit edit mode visually */ }}>✓</button>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontSize: 15, fontWeight: 800 }}>${fob.toLocaleString()}</span>
                      {!dec && <button className="btn btn-secondary btn-sm" style={{ fontSize: 10 }}
                        onClick={() => setAdjusting(prev => ({ ...prev, [u.unit_id]: String(fob) }))}>✏️ Adjust</button>}
                    </div>
                  )}
                  <span style={{ marginLeft: 'auto', fontWeight: 700, color: mc.color, fontSize: 12 }}>
                    Margin: {margin >= 0 ? '+' : ''}${Math.round(fob - cost).toLocaleString()} ({margin.toFixed(1)}%)
                  </span>
                </div>

                {u.accountant_notes && (
                  <div style={{ fontSize: 11, color: 'var(--text3)', fontStyle: 'italic', marginTop: 6 }}>Accountant: "{u.accountant_notes}"</div>
                )}

                {/* Per-unit action buttons */}
                {!dec && (
                  <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                    <button className="btn btn-success btn-sm" onClick={() => approveUnit(u.unit_id)}>✅ Approve</button>
                    <button className="btn btn-primary btn-sm" onClick={() => {
                      setAdjusting(prev => ({ ...prev, [u.unit_id]: String(fob) }));
                    }}>✏️ Adjust & Approve</button>
                    <button className="btn btn-secondary btn-sm" style={{ color: 'var(--red)', borderColor: 'var(--red)' }}
                      onClick={() => setRejectingUnit(u.unit_id)}>❌ Reject</button>
                  </div>
                )}

                {/* Inline reject reason */}
                {rejectingUnit === u.unit_id && (
                  <div style={{ marginTop: 8, padding: 10, background: 'rgba(248,81,73,0.06)', borderRadius: 8, border: '1px solid rgba(248,81,73,0.2)' }}>
                    <label className="form-label" style={{ fontSize: 11 }}>Reason for rejection</label>
                    <input className="form-input" placeholder="e.g. Margin too low, needs minimum $3,800 FOB"
                      onKeyDown={e => { if (e.key === 'Enter' && e.target.value.trim()) rejectUnit(u.unit_id, e.target.value.trim()); }}
                      style={{ fontSize: 12, marginBottom: 6 }} />
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button className="btn btn-danger btn-sm" onClick={() => {
                        const input = document.querySelector(`[data-reject-input="${u.unit_id}"]`);
                        rejectUnit(u.unit_id, input?.value || 'Price adjustment needed');
                      }}>Confirm Reject</button>
                      <button className="btn btn-secondary btn-sm" onClick={() => setRejectingUnit(null)}>Cancel</button>
                    </div>
                  </div>
                )}

                {/* Decision display */}
                {dec === 'approved' && (
                  <div style={{ marginTop: 8, fontSize: 12, color: 'var(--green)', fontWeight: 600 }}>
                    ✅ Approved{adjusting[u.unit_id] !== undefined ? ` (adjusted to $${getFob(u).toLocaleString()})` : ` at $${fob.toLocaleString()}`}
                    <button className="btn btn-secondary btn-sm" style={{ marginLeft: 8, fontSize: 9 }}
                      onClick={() => setDecisions(prev => { const n = { ...prev }; delete n[u.unit_id]; return n; })}>Undo</button>
                  </div>
                )}
                {dec === 'rejected' && (
                  <div style={{ marginTop: 8, fontSize: 12, color: 'var(--red)' }}>
                    ❌ Rejected: {rejectReasons[u.unit_id] || 'No reason'}
                    <button className="btn btn-secondary btn-sm" style={{ marginLeft: 8, fontSize: 9 }}
                      onClick={() => setDecisions(prev => { const n = { ...prev }; delete n[u.unit_id]; return n; })}>Undo</button>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}

      {/* ── Summary bar ── */}
      <div style={{ padding: 16, background: 'var(--bg3)', borderRadius: 10, border: '1px solid var(--border)', marginBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 8 }}>
          <span>Total Cost: <strong>${Math.round(totalCost).toLocaleString()}</strong></span>
          <span>Total FOB: <strong>${Math.round(totalFob).toLocaleString()}</strong></span>
          <span>Avg Margin: <strong style={{ color: marginColor(avgMargin).color }}>{avgMargin.toFixed(1)}%</strong></span>
        </div>
        <div style={{ fontSize: 12, color: 'var(--text3)' }}>
          {approvedCount > 0 && <span style={{ color: 'var(--green)', fontWeight: 600, marginRight: 12 }}>✅ {approvedCount} approved</span>}
          {rejectedCount > 0 && <span style={{ color: 'var(--red)', fontWeight: 600, marginRight: 12 }}>❌ {rejectedCount} rejected</span>}
          {undecidedCount > 0 && <span style={{ color: 'var(--yellow)', fontWeight: 600 }}>⏳ {undecidedCount} undecided</span>}
        </div>
      </div>

      {/* Admin notes */}
      <div className="form-group" style={{ marginBottom: 16 }}>
        <label className="form-label">Admin Notes to Accountant (optional)</label>
        <textarea className="form-input" rows={2} value={adminNotes} onChange={e => setAdminNotes(e.target.value)}
          placeholder="e.g. Hilux needs at least $3,800. Rest are fine." style={{ resize: 'vertical', fontSize: 12 }} />
      </div>

      {/* Send back entire quote form */}
      {sendingBack && (
        <div style={{ padding: 16, background: 'rgba(248,81,73,0.06)', border: '1px solid rgba(248,81,73,0.2)', borderRadius: 10, marginBottom: 16 }}>
          <label className="form-label">Why is this quote being sent back?</label>
          <textarea className="form-input" rows={2} value={revisionNote} onChange={e => setRevisionNote(e.target.value)}
            placeholder="Explain what needs to change..." style={{ resize: 'vertical', marginBottom: 8 }} />
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-danger" disabled={!revisionNote.trim()} onClick={sendBackAll}>Send Back Entire Quote</button>
            <button className="btn btn-secondary" onClick={() => setSendingBack(false)}>Cancel</button>
          </div>
        </div>
      )}

      {/* Action buttons */}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        {undecidedCount > 0 && (
          <button className="btn btn-success" onClick={approveAllRemaining}>
            <CheckIcon /> Approve All{approvedCount > 0 ? ' Remaining' : ''}
          </button>
        )}
        <button className="btn btn-success" style={{ flex: 1 }} disabled={undecidedCount > 0 && approvedCount === 0 && rejectedCount === 0} onClick={finalize}>
          <CheckIcon /> Finalize Approvals
        </button>
        {!sendingBack && (
          <button className="btn btn-secondary" onClick={() => setSendingBack(true)}>
            🔄 Send Back All
          </button>
        )}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// MAIN PAGE — PriceQuotes
// ══════════════════════════════════════════════════════════════
function PriceQuotes({ user, custFilter, setCustFilter }) {
  const { t } = useLanguage();
  const [activeQuote, setActiveQuote] = useState(null); // { quote, mode: 'price' | 'approve' }
  const [, forceUpdate] = useState();

  const isAdmin = user.role === 'head_admin' || user.role === 'admin';
  const isAccountant = user.role === 'accountant';

  const allQuotes = DB.get('priceQuotes') || [];
  const customers = DB.get('customers') || [];

  // Filter: batch quotes (have units array) + legacy single quotes
  const quotes = (custFilter ? allQuotes.filter(q => (q.customerId || q.requestedBy) === custFilter) : allQuotes);

  // Group by status
  const pending = quotes.filter(q => q.status === 'pending');
  const inProgress = quotes.filter(q => q.status === 'in_progress' || q.status === 'revision_needed' || q.status === 'partially_approved');
  const pendingApproval = quotes.filter(q => q.status === 'pending_approval');
  const approved = quotes.filter(q => q.status === 'approved' || q.status === 'quoted');

  const getCust = (q) => customers.find(c => c.id === (q.customerId || q.requestedBy));

  // Accountant picks up a quote
  const startPricing = (quote) => {
    const updated = { ...quote, status: 'in_progress', assigned_to: user.id, assignedToName: user.name };
    DB.set('priceQuotes', allQuotes.map(q => q.id === quote.id ? updated : q));
    setActiveQuote({ quote: updated, mode: 'price' });
  };

  // ── Active view ──
  if (activeQuote) {
    const { quote: q, mode } = activeQuote;
    if (mode === 'price' && Array.isArray(q.units)) {
      return <AccountantPricingView quote={q} user={user} onBack={() => { setActiveQuote(null); forceUpdate({}); }} />;
    }
    if (mode === 'approve' && Array.isArray(q.units)) {
      return <AdminApprovalView quote={q} user={user} onBack={() => { setActiveQuote(null); forceUpdate({}); }} />;
    }
    // Legacy single-unit fallback — just go back
    setActiveQuote(null);
  }

  // ── Quote card renderer ──
  const renderQuoteCard = (quote) => {
    const cust = getCust(quote);
    const isBatch = Array.isArray(quote.units) && quote.units.length > 0;
    const unitCount = isBatch ? quote.units.length : 1;
    const pricedCount = isBatch ? quote.units.filter(u => u.fob_price || u.pricing?.fob_price).length : (quote.fobPriceUsd ? 1 : 0);

    return (
      <div key={quote.id} className="card" style={{ marginBottom: 8 }}>
        <div className="card-body" style={{ padding: '14px 16px' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <span style={{ fontWeight: 700 }}>{cust?.name || quote.requestedByName}</span>
                {cust?.country && <span style={{ fontSize: 11, color: 'var(--text3)' }}>({cust.country})</span>}
                <span className={`status status-${quote.status === 'approved' || quote.status === 'quoted' ? 'available' : quote.status === 'pending_approval' ? 'transit' : 'pending'}`} style={{ fontSize: 9 }}>
                  {quote.status === 'pending_approval' ? 'Awaiting Approval' : (quote.status || '').replace(/_/g, ' ')}
                </span>
              </div>
              <div style={{ fontSize: 11, color: 'var(--cyan)', fontFamily: 'monospace', marginTop: 2 }}>
                #{quote.id} — {unitCount} unit{unitCount !== 1 ? 's' : ''} · {quote.createdAt}
              </div>
              {quote.customer_notes && (
                <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 4, fontStyle: 'italic' }}>
                  Customer: "{quote.customer_notes}"
                </div>
              )}
              {quote.revision_notes && (
                <div style={{ fontSize: 11, color: 'var(--red)', marginTop: 4 }}>
                  Revision needed: "{quote.revision_notes}"
                </div>
              )}

              {/* Unit list preview */}
              {isBatch && (
                <div style={{ marginTop: 8 }}>
                  {quote.units.slice(0, 4).map((u, i) => (
                    <div key={u.unit_id || i} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, padding: '2px 0', color: 'var(--text2)' }}>
                      <span style={{ width: 14 }}>{getUnitIcon(u.unit_type)}</span>
                      <span style={{ flex: 1 }}>{u.unit_description}</span>
                      {(u.fob_price || u.pricing?.fob_price) ? (
                        <span style={{ color: 'var(--green)', fontWeight: 600 }}>FOB: ${(u.fob_price || u.pricing?.fob_price || 0).toLocaleString()}</span>
                      ) : (
                        <span style={{ color: 'var(--text3)' }}>not set</span>
                      )}
                    </div>
                  ))}
                  {quote.units.length > 4 && <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 2 }}>+{quote.units.length - 4} more</div>}
                </div>
              )}

              {/* Legacy single-unit display */}
              {!isBatch && quote.unitDescription && (
                <div style={{ fontSize: 12, color: 'var(--text2)', marginTop: 4 }}>
                  {quote.unitDescription} · {quote.unitSku}
                  {quote.fobPriceUsd > 0 && <span style={{ color: 'var(--green)', fontWeight: 700, marginLeft: 8 }}>${quote.fobPriceUsd.toLocaleString()}</span>}
                </div>
              )}
            </div>

            {/* Actions */}
            <div style={{ flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 4 }}>
              {/* Accountant: pick up pending or continue in_progress */}
              {(isAccountant || isAdmin) && quote.status === 'pending' && isBatch && (
                <button className="btn btn-primary btn-sm" onClick={() => startPricing(quote)}>Open & Start Pricing</button>
              )}
              {(isAccountant || isAdmin) && ['in_progress', 'revision_needed', 'partially_approved'].includes(quote.status) && isBatch && (
                <button className="btn btn-secondary btn-sm" onClick={() => setActiveQuote({ quote, mode: 'price' })}>
                  {quote.status === 'revision_needed' ? 'Revise Pricing' : quote.status === 'partially_approved' ? 'Revise Rejected Units' : 'Continue Pricing'}
                </button>
              )}

              {/* Admin: approve pending_approval */}
              {isAdmin && quote.status === 'pending_approval' && isBatch && (
                <button className="btn btn-success btn-sm" onClick={() => setActiveQuote({ quote, mode: 'approve' })}>
                  Review & Approve
                </button>
              )}

              {/* Admin: delete quote */}
              {isAdmin && (
                <button className="btn btn-secondary btn-sm" style={{ color: 'var(--red)', borderColor: 'var(--red)', fontSize: 10 }} onClick={() => {
                  if (!window.confirm(`Delete this price quote from ${quote.customerName || 'customer'}?`)) return;
                  const all = DB.get('priceQuotes') || [];
                  DB.set('priceQuotes', all.filter(q => q.id !== quote.id));
                  forceUpdate(v => v + 1);
                }}>✕ Delete</button>
              )}

              {/* Priced count badge */}
              {isBatch && pricedCount > 0 && (
                <span style={{ fontSize: 10, color: pricedCount === unitCount ? 'var(--green)' : 'var(--yellow)', fontWeight: 600 }}>
                  {pricedCount}/{unitCount} priced
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderSection = (title, items, badge) => {
    if (items.length === 0) return null;
    return (
      <div style={{ marginBottom: 24 }}>
        <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 10, display: 'flex', alignItems: 'center', gap: 8 }}>
          {title}
          <span style={{ fontSize: 10, background: badge.bg, color: badge.color, padding: '2px 8px', borderRadius: 10, fontWeight: 700 }}>{items.length}</span>
        </div>
        {items.map(q => renderQuoteCard(q))}
      </div>
    );
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14, flexWrap: 'wrap', gap: 10 }}>
        <h2>{t('priceQuotes')}</h2>
        <CustomerFilter value={custFilter} onChange={setCustFilter} />
      </div>
      <CustomerFilterBanner value={custFilter} onChange={setCustFilter} />

      {quotes.length === 0 ? (
        <div className="empty">
          <DollarSignIcon />
          <div className="empty-title">No Price Quotes</div>
          <div className="empty-text">Customer quote requests will appear here.</div>
        </div>
      ) : (
        <>
          {renderSection('Awaiting Approval', pendingApproval, { bg: 'rgba(163,113,247,0.15)', color: 'var(--purple)' })}
          {renderSection('Pending', pending, { bg: 'rgba(248,81,73,0.15)', color: 'var(--red)' })}
          {renderSection('In Progress / Revision Needed', inProgress, { bg: 'rgba(88,166,255,0.15)', color: 'var(--blue)' })}
          {renderSection('Completed', [...approved].sort((a, b) => (b.approved_at || b.quotedAt || '').localeCompare(a.approved_at || a.quotedAt || '')), { bg: 'rgba(63,185,80,0.15)', color: 'var(--green)' })}
        </>
      )}
    </div>
  );
}

export default PriceQuotes;
